<script setup lang="ts">
import { ArrowLeft, ArrowRight, CircleArrowLeft, CircleArrowRight } from '@lucide/vue'
import { ACTIVE_RUN_STATUSES } from '@zerohuman/shared'
import {
    Button,
    MOTION,
    type RunStepTone,
    type RunStepView,
    ZhJson,
    ZhPageTitle,
    ZhPanel,
    ZhRunTimeline,
    ZhSystemLabel,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
    api,
    blockedReason,
    type CeoQuestionAnswer,
    failReason,
    formatDuration,
    formatGatePayload,
    type GateDecision,
    type GateRow,
    gateChoiceLabel,
    type PlanRow,
    type RunDetail,
    type RunFlowLink,
    runStoppedLine,
    skipReason,
} from '@/api'
import ExecutionResources from '@/components/ExecutionResources.vue'
import GateDecisionCard from '@/components/GateDecisionCard.vue'
import GatePayloadEditor from '@/components/GatePayloadEditor.vue'
import RetryCorrectionForm from '@/components/RetryCorrectionForm.vue'
import RunSignInPanel from '@/components/RunSignInPanel.vue'
import { GATE_CARD_CLOSE_MS, useGateDecisions } from '@/composables/useGateDecisions'
import { useLiveResource } from '@/composables/useLiveResource'
import { useUiMode } from '@/composables/useUiMode'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { costBreakdown, formatUsdCost } from '@/lib/cost'
import { reportNeedsYouItemViewed } from '@/lib/needsYouReport'
import { ownerVersionLabel } from '@/lib/ownerVersion'
import { errorReason, toolLabel } from '@/lib/presentation'
import { type RetryValues, refusedField, retryCorrection, retryFields, retryValues } from '@/lib/retryCorrection'
import { runPillStatus } from '@/lib/statusTone'
import { viewedOncePerPage } from '@/lib/viewedOncePerPage'

const route = useRoute()
const router = useRouter()
/**
 * #1749: Standard drops a failed run's leading error code and names an edited gate's tool by its vendor; Advanced
 * shows the whole reason and the raw tool name. The step payloads stay as stored in both (what was approved).
 */
const { mode: uiMode } = useUiMode()
const runId = computed(() => String(route.params.id))
/** A refused action, in words. The next read of the run clears it: the run has likely moved on. */
const actionError = ref<string | null>(null)
const busy = ref(false)
/**
 * The plan this run produced, if it produced one (#227): a proposing run's output is a plan, and the plan's
 * own page is where it can be read in full and shared, so the run links straight at it.
 */
const plan = ref<PlanRow | null>(null)
let clock: ReturnType<typeof setInterval> | null = null
const now = ref(Date.now())

const active = new Set<string>(['pending', ...ACTIVE_RUN_STATUSES])
/**
 * Open work a human can drop: everything active, plus blocked runs (which hold no lock, and Blockers can still
 * resume). The same statuses as the API's `OPEN_EXECUTION_STATUSES`; a run in none of them is over.
 */
const cancellable = new Set([...active, 'blocked'])
const retryable = new Set(['failed', 'cancelled', 'skipped_overlap', 'succeeded', 'blocked'])
const log = ref<string | null>(null)
/** The log is collapsed until asked for, and stays fetched once open. */
const logOpen = ref(false)
/** A decided gate's card shows the decision, fades, and closes up its space before it leaves the page. */
const decisions = useGateDecisions(MOTION.decisionMs + GATE_CARD_CLOSE_MS)

/**
 * The run is read every three seconds while anything on the page can still change, never two reads at once and
 * not while the tab is hidden, and each read after the first asks only for the events since the last one the
 * page holds. A long run's detail takes seconds to build and is nearly all `tool.*` payloads: read every second,
 * reads stacked up on the database faster than it answered them, and a late one could land over a newer one.
 */
const RUN_READ_MS = 3000
const {
    data: detail,
    error: readError,
    updatedAt,
    refresh,
} = useLiveResource<RunDetail>(
    () => runId.value,
    (signal, known) => readRun(runId.value, known, signal),
    // A refused action means the page is behind the run, so it reads again even once the run is over.
    { everyMs: RUN_READ_MS, active: (): boolean => !finished.value || actionError.value !== null },
)
const error = computed(() => actionError.value ?? (readError.value ? 'Run not found.' : null))

/**
 * Each waiting gate this page shows is a Needs-you item opened here (#1774): reported viewed once per gate id while the
 * page is open, however often the run is read again. A decided gate is no item; the server says which waiting ones
 * are the CEO's. A failed report changes nothing on the page.
 */
const reportViewed = viewedOncePerPage(reportNeedsYouItemViewed)
watch(
    () => (detail.value?.gates ?? []).filter(gate => gate.status === 'waiting').map(gate => gate.id),
    gateIds => {
        for (const gateId of gateIds) {
            reportViewed(gateId)
        }
    },
    { immediate: true },
)

/** The run whose last read changed nothing: whatever the worker records on the way out has landed. */
const quietRunId = ref<string | null>(null)

async function readRun(id: string, known: RunDetail | null, signal: AbortSignal): Promise<RunDetail> {
    const held = known?.run.id === id ? known : null
    const read = await api.run(id, { afterEventId: held?.events.at(-1)?.id }, signal)
    const next = held ? foldRead(held, read) : read
    quietRunId.value = next === held ? id : null
    return next
}

/**
 * A later read folded into what the page holds. Its events are the ones since the last the page holds, plus any
 * sharing that one's second, so only the new ones are added and every event already drawn keeps its object:
 * its payload is not parsed again. The rest is small and taken as read, except that gates which have not
 * changed keep theirs, and a read that changed nothing leaves the page exactly as it was.
 */
function foldRead(held: RunDetail, read: RunDetail): RunDetail {
    const ids = new Set(held.events.map(event => event.id))
    const added = read.events.filter(event => !ids.has(event.id))
    const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
    if (added.length === 0 && same({ ...read, events: null }, { ...held, events: null })) {
        return held
    }
    return {
        ...read,
        events: added.length > 0 ? [...held.events, ...added] : held.events,
        gates: same(read.gates, held.gates) ? held.gates : read.gates,
    }
}

/**
 * Whether nothing on the page can change any more, so it stops reading: the run is over, no successor it
 * started is still on its way (its row says where each one is), and a read of it has come back unchanged. The
 * worker settles a run's status before it records the last event, the log and the successors it starts.
 */
const finished = computed<boolean>(() => {
    const current = detail.value
    if (!current || cancellable.has(current.run.status)) {
        return false
    }
    const moving = (current.flow?.next ?? []).some(link => link.status !== null && active.has(link.status))
    return !moving && quietRunId.value === current.run.id
})

/** Whether the run itself is still open; null until the page has read it. */
const runOpen = computed(() => (detail.value ? cancellable.has(detail.value.run.status) : null))

// Following a workflow link keeps this page and changes only the id: nothing from the last run may carry over.
watch(
    runId,
    id => {
        log.value = null
        logOpen.value = false
        plan.value = null
        actionError.value = null
        quietRunId.value = null
        void loadPlan(id)
    },
    { immediate: true },
)
// A plan is the run's output, so a run the page watched working may only have one once it is over.
watch(runOpen, (isOpen, wasOpen) => {
    if (wasOpen && isOpen === false) {
        void loadPlan(runId.value)
    }
})
watch(updatedAt, () => {
    actionError.value = null
})

/** One step per run event, newest first, so the latest step (and why the run stopped) is at the top. */
type TimelineStep = {
    id: string
    type: string
    at: string
    payload: unknown
    gate: GateRow | null
    reason: Terminal | null
}

/** Why the run stopped, and the tone its step takes: a skip is a note, a failure or a block is failed. */
type Terminal = { text: string; tone: RunStepTone }

const scheduled = computed(() => {
    const run = detail.value?.run
    if (run?.status !== 'pending' || !run.notBefore) {
        return null
    }
    const at = new Date(run.notBefore)
    return { at: at.toLocaleString(), in: formatDuration(at.getTime() - now.value) }
})
const timing = computed(() => {
    const run = detail.value?.run
    if (!run?.startedAt) {
        return null
    }
    const started = new Date(run.startedAt)
    if (run.finishedAt && !active.has(run.status)) {
        return {
            live: false,
            startedAt: started.toLocaleString(),
            duration: formatDuration(new Date(run.finishedAt).getTime() - started.getTime()),
        }
    }
    if (!active.has(run.status)) {
        return null
    }
    return { live: true, startedAt: started.toLocaleString(), duration: formatDuration(now.value - started.getTime()) }
})
/**
 * What the run has cost so far — the same figure its spend caps read — and how it splits between per-token
 * spend and a share of a subscription seat. The page reads the run every few seconds, so it climbs while the
 * run works.
 */
const cost = computed(() => {
    const current = detail.value
    if (!current) {
        return null
    }
    const total = current.cost?.totalCents ?? current.run.costUsdCents ?? 0
    return { amount: formatUsdCost(total), lines: current.cost ? costBreakdown(current.cost) : [] }
})

/** Where a neighbouring run is, in a few words: when it starts, what it waits on, or how it ended. */
function flowState(link: RunFlowLink): string {
    if (!link.status) {
        return unstartedState(link)
    }
    const pending = link.status === 'pending' ? pendingState(link) : null
    if (pending) {
        return pending
    }
    if (link.startedAt && active.has(link.status)) {
        return `${link.status} · ${formatDuration(now.value - new Date(link.startedAt).getTime())}`
    }
    return link.status
}

/** A successor edge with no run: why it never started, or whether it can still fire while this run works. */
function unstartedState(link: RunFlowLink): string {
    if (link.reason) {
        return `not started: ${link.reason}`
    }
    return detail.value && active.has(detail.value.run.status) ? 'not yet' : 'not taken'
}

function pendingState(link: RunFlowLink): string | null {
    if (link.notBefore) {
        const wait = new Date(link.notBefore).getTime() - now.value
        return wait > 0 ? `starts in ${formatDuration(wait)}` : 'starting'
    }
    if (link.waitingReason === 'overlap') {
        return 'waits for the task’s current run'
    }
    return link.waitingReason === 'member_busy' ? 'waits for its member to be free' : null
}

const INFERRED_TITLE = 'Linked by timing: this run did not record the hop (an older run, or a worker on older code)'

/** The taken successor is lit by the kit's complete-state token, so the screen names the path taken and not its hue. */
const TAKEN_FILL = 'bg-state-complete/5'

const ON_LABELS: Record<string, string> = { terminal: 'always', succeeded: 'success', failed: 'failure' }

/** The outcomes a successor edge fires on, in the words a person reads: `on ci_red / ci_missing`. */
function flowCondition(link: RunFlowLink): string | null {
    const on = link.on ?? []
    return on.length > 0 ? `on ${on.map(value => ON_LABELS[value] ?? value).join(' / ')}` : null
}

function flowName(link: RunFlowLink): string {
    return link.task?.name ?? link.slug ?? 'Deleted task'
}

const previousRun = computed(() => detail.value?.flow?.previous ?? null)
/** Every successor edge of this run's task version: the ones it started link to their run; the rest say why not. */
const nextRuns = computed(() =>
    (detail.value?.flow?.next ?? []).map((link, index) => ({
        key: link.runId ?? `${link.slug}-${index}`,
        link,
        name: flowName(link),
        state: flowState(link),
        condition: flowCondition(link),
        taken: link.runId !== null,
    })),
)

/** Why the run stopped, in the same words the lists use. Null while it is still working. */
const terminalReason = computed<Terminal | null>(() => {
    const current = detail.value
    if (!current) {
        return null
    }
    const skipped = skipReason(current.run)
    if (skipped) {
        return { text: `Skipped: ${skipped}`, tone: 'note' }
    }
    const blocked = blockedReason(current.run)
    if (blocked) {
        return { text: `Blocked: ${blocked}`, tone: 'failed' }
    }
    const failed = failReason(current.run, current.events ?? [])
    if (failed) {
        return { text: `Failed: ${errorReason(failed, uiMode.value)}`, tone: 'failed' }
    }
    // Succeeded on an outcome its task has no next step for: the pipeline stopped here, as Blockers says.
    const stopped = runStoppedLine(current.run)
    return stopped ? { text: stopped, tone: 'failed' } : null
})

/**
 * The status the title reads: a run that dead-ended is stored `succeeded` but stopped its pipeline, so it reads
 * "stopped" (`runPillStatus`), with why in `terminalReason`.
 */
const shownStatus = computed(() => (detail.value ? runPillStatus(detail.value.run) : ''))

/** The gate a `gate.*` event is about, of those still on the page (waiting, or showing your decision). */
function gateFor(event: RunDetail['events'][number], gates: GateRow[]): GateRow | null {
    const payload = event.payload
    const gateId = payload && typeof payload.gateId === 'string' ? payload.gateId : null
    if (gateId) {
        return gates.find(gate => gate.id === gateId) ?? null
    }
    const tool = payload && typeof payload.tool === 'string' ? payload.tool : null
    if (tool && event.type.startsWith('gate.')) {
        return gates.find(gate => gate.toolName === tool) ?? null
    }
    return null
}

const timeline = computed<TimelineStep[]>(() => {
    const current = detail.value
    if (!current) {
        return []
    }
    const gates = (current.gates ?? []).filter(decisions.onPage)
    const events = [...(current.events ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    const reason = terminalReason.value
    const steps = events.map((event, index) => ({
        id: event.id,
        type: event.type,
        at: new Date(event.createdAt).toLocaleTimeString(),
        payload: event.payload ?? null,
        gate: gateFor(event, gates),
        reason: index === 0 ? reason : null,
    }))
    // A gate is decided in the step that asked for it, the oldest that names it; never again in a later step
    // about it (gate.aged, or gate.approved arriving while the decided card still fades).
    const asked = new Set<string>()
    for (const step of [...steps].reverse()) {
        if (step.gate && asked.has(step.gate.id)) {
            step.gate = null
        } else if (step.gate) {
            asked.add(step.gate.id)
        }
    }
    return steps
})

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * #1473: the record keeps a tool result over the storage limit as its start and its true size
 * (`{ truncated: true, chars, preview }`). Said in words on the step, so a reader of the run never takes that copy
 * for the whole result; a whole result says nothing.
 */
function shortenedNote(payload: unknown): string | undefined {
    const result = isRecord(payload) ? payload['result'] : undefined
    if (!isRecord(result) || result['truncated'] !== true || typeof result['chars'] !== 'number') {
        return undefined
    }
    return `Shortened copy: the result was ${result['chars'].toLocaleString('en-GB')} characters, and only its start is kept here.`
}

/**
 * The timeline as the shared record draws it (ZhRunTimeline): a step waiting on a gate takes the action
 * tone and offers the decision; the newest step says in words why the run stopped, in that reason's tone.
 * A step whose tool result the record keeps shortened says so (`shortenedNote`).
 */
const steps = computed<RunStepView[]>(() =>
    timeline.value.map(step => ({
        id: step.id,
        type: step.type,
        at: step.at,
        title: step.reason?.text,
        detail: shortenedNote(step.payload),
        tone: step.gate ? 'action' : (step.reason?.tone ?? 'done'),
        decide: step.gate !== null,
    })),
)
const stepsById = computed(() => new Map(timeline.value.map(step => [step.id, step])))

function stepOf(view: RunStepView): TimelineStep | undefined {
    return view.id ? stepsById.value.get(view.id) : undefined
}

/** The gate a step is still waiting at, if any: the decision the step offers. */
function gateOf(view: RunStepView): GateRow | null {
    return stepOf(view)?.gate ?? null
}

/**
 * The sign-in the run is waiting on, if any (os#1498): a `<prefix>.sign_in` gate still open. A person signs in
 * from its own panel rather than by deciding it, so the run goes on from the signed-in page.
 */
const signInGate = computed(
    () =>
        (detail.value?.gates ?? []).find(
            gate => gate.status === 'waiting' && /^[^.]+\.sign_in$/i.test(gate.toolName),
        ) ?? null,
)

/** Waiting gates with no event of their own still need a decision, so they keep their own block. */
const unlinkedGates = computed(() => {
    const linked = new Set(timeline.value.map(step => step.gate?.id).filter(Boolean))
    return (detail.value?.gates ?? []).filter(gate => decisions.onPage(gate) && !linked.has(gate.id))
})

/**
 * Gates the CEO edited before approving (os#911): once decided, the run shows what it drafted and what was
 * actually approved, side by side, so the edit is part of the run's record rather than something only the
 * approver saw. A gate approved untouched holds no edit and appears here not at all, and neither does one
 * that was rejected or sent back: only an approved gate ran anything, so only it can say "approved with an
 * edit" (the API refuses an edit on any other decision, but the page does not rely on that alone).
 */
const editedGates = computed(() =>
    (detail.value?.gates ?? []).filter(
        gate =>
            gate.status === 'approved' &&
            gate.editedPayload !== null &&
            gate.editedPayload !== undefined &&
            typeof gate.editedPayload === 'object' &&
            gate.payload !== null &&
            typeof gate.payload === 'object',
    ),
)

onMounted(() => {
    clock = setInterval(() => {
        now.value = Date.now()
    }, 250)
})

onUnmounted(() => {
    if (clock) {
        clearInterval(clock)
    }
})

/**
 * Best-effort (#227): most runs produce no plan, and a run that can't be read for its plans is still a
 * perfectly readable run — so a failure here leaves the page as it is rather than surfacing an error. Not
 * awaited by anything: whether this run produced a plan is a side note, never a reason to hold the page.
 */
async function loadPlan(id: string): Promise<void> {
    try {
        const rows = await api.plans({ producedByRunId: id })
        // A late answer about the run the page has since left is not this run's plan.
        if (id === runId.value) {
            plan.value = rows[0] ?? null
        }
    } catch {
        // Leave the last-known plan (usually none); the rest of the run page is unaffected.
    }
}

/**
 * The retry correction form (#1132): a run whose task declares inputs is retried through it, prefilled from the
 * stored input, so a person can supply what the stored input lacks instead of starting the same doomed run again.
 */
const retryOpen = ref(false)
const retryForm = ref<RetryValues>({})
/** The server's field-level refusal, against the field it names. */
const retryErrors = ref<Record<string, string>>({})
const retryInputs = computed(() => retryFields(detail.value?.inputs))

watch(runId, () => {
    retryOpen.value = false
    retryErrors.value = {}
})

async function retry(): Promise<void> {
    if (!detail.value) {
        return
    }
    if (retryInputs.value.length > 0) {
        retryForm.value = retryValues(detail.value.inputs, detail.value.run.input)
        retryErrors.value = {}
        retryOpen.value = true
        return
    }
    await retryWith(null)
}

async function submitRetry(): Promise<void> {
    if (!detail.value) {
        return
    }
    await retryWith(retryCorrection(detail.value.inputs, retryForm.value))
}

async function retryWith(correction: Record<string, unknown> | null): Promise<void> {
    if (!detail.value) {
        return
    }
    // #1796: a retry starts model work, so without an AI account nothing is sent and the prompt opens.
    if (!(await requireAiAccount())) return
    const id = detail.value.run.id
    busy.value = true
    retryErrors.value = {}
    try {
        const next = correction ? await api.retryRun(id, correction) : await api.retryRun(id)
        retryOpen.value = false
        // The new run's id is a new key, which reads it.
        await router.replace(`/runs/${next.id}`)
    } catch (caught) {
        const field = refusedField(caught)
        if (retryOpen.value && field && retryInputs.value.some(input => input.key === field)) {
            retryErrors.value = { [field]: caught instanceof Error ? caught.message : String(caught) }
        } else {
            actionError.value = 'Could not retry (still active, or overlap).'
        }
    } finally {
        busy.value = false
    }
}

async function cancel(): Promise<void> {
    if (!detail.value) {
        return
    }
    busy.value = true
    try {
        await api.cancelRun(detail.value.run.id)
        await refresh()
    } catch {
        actionError.value = 'Could not cancel (already finished?).'
    } finally {
        busy.value = false
    }
}

async function loadLog(): Promise<void> {
    const id = detail.value?.run.id
    if (!id) {
        return
    }
    try {
        const row = await api.runLog(id)
        // Only this run's log: the page may have followed a link while it was on its way.
        if (id === runId.value) {
            log.value = row.body
        }
    } catch {
        actionError.value = 'Log not stored yet.'
    }
}

/** Show / hide the log. It is fetched once; hiding and showing again does not re-fetch. */
async function toggleLog(): Promise<void> {
    if (logOpen.value) {
        logOpen.value = false
        return
    }
    if (log.value === null) {
        await loadLog()
        if (log.value === null) {
            return
        }
    }
    logOpen.value = true
}

/**
 * A decision shows on the gate's card, which fades and closes up without waiting on the API; if the API
 * refuses it, the card comes back as it was. The run is read again once the card has gone.
 */
async function decide(
    id: string,
    decision: GateDecision,
    note: string,
    answers?: CeoQuestionAnswer[],
    /** The payload as the CEO edited it before approving (os#911). Absent when they changed nothing. */
    edit?: Record<string, unknown>,
    /** The key of the choice the decision was made through, on a gate that offers choices. */
    choice?: string,
): Promise<void> {
    // #1796: a decision resumes the run it holds, which is model work, so without an AI account nothing is sent and
    // the prompt opens.
    if (!(await requireAiAccount())) return
    busy.value = true
    // An untouched approval is sent exactly as it was before editing existed: no edit in the request at all.
    // The same holds for a choice: only a gate that offers choices sends one.
    const plain = edit
        ? () => api.decideGate(id, decision, note, answers, edit)
        : () => api.decideGate(id, decision, note, answers)
    const send = choice ? () => api.decideGate(id, decision, note, undefined, undefined, choice) : plain
    const gate = (detail.value?.gates ?? []).find(row => row.id === id)
    try {
        if (await decisions.decide(id, decision, send, gate && gateChoiceLabel(gate, choice))) {
            await refresh()
        } else {
            actionError.value = 'Could not record decision (already decided?).'
        }
    } finally {
        busy.value = false
    }
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<template v-else-if="detail">
			<!-- Top row: where the run sits in its workflow, and what it has cost so far (top right). -->
			<div class="flex items-start gap-4">
				<!--
					The run's place in its workflow: predecessor (left) and successors (right). Each card is the kit
					panel with the kit system label for its heading; a linked run is an interactive kit panel, and the
					predecessor and the taken path take their accent from the state tokens, never a hue stated here.
				-->
				<div
					v-if="previousRun?.runId || nextRuns.length"
					class="grid min-w-0 flex-1 gap-3"
					:class="previousRun?.runId && nextRuns.length ? 'sm:grid-cols-2' : ''"
				>
					<ZhPanel v-if="previousRun?.runId" data-testid="run-flow-previous" class="space-y-2">
						<ZhSystemLabel tone="system" class="flex items-center gap-1.5">
							<CircleArrowLeft class="size-4" aria-hidden="true" /> Predecessor
						</ZhSystemLabel>
						<ZhTooltip :text="previousRun.inferred ? INFERRED_TITLE : null">
							<RouterLink v-slot="{ href, navigate }" :to="`/runs/${previousRun.runId}`" custom>
								<ZhPanel
									as="a"
									interactive
									data-testid="run-flow-previous-node"
									:href="href"
									class="flex items-center gap-3 text-sm"
									@click="navigate"
								>
									<ArrowLeft class="text-state-system size-4 shrink-0" aria-hidden="true" />
									<span class="font-medium">{{ flowName(previousRun) }}</span>
									<span class="text-muted-foreground ml-auto tabular-nums">{{ flowState(previousRun) }}</span>
								</ZhPanel>
							</RouterLink>
						</ZhTooltip>
					</ZhPanel>
					<Transition enter-active-class="transition duration-300 ease-out" enter-from-class="-translate-y-2 opacity-0">
						<ZhPanel v-if="nextRuns.length" data-testid="run-flow-next" class="space-y-2">
							<ZhSystemLabel tone="muted" class="flex items-center gap-1.5">
								<CircleArrowRight class="size-4" aria-hidden="true" /> {{ nextRuns.length > 1 ? 'Successors' : 'Successor' }}
							</ZhSystemLabel>
							<ul class="space-y-1">
								<li
									v-for="node in nextRuns"
									:key="node.key"
									data-testid="run-flow-next-node"
									:data-taken="node.taken ? 'true' : 'false'"
								>
									<ZhTooltip v-if="node.link.runId" :text="node.link.inferred ? INFERRED_TITLE : null">
										<RouterLink v-slot="{ href, navigate }" :to="`/runs/${node.link.runId}`" custom>
											<ZhPanel
												as="a"
												interactive
												:href="href"
												class="flex items-center gap-3 text-sm"
												:class="TAKEN_FILL"
												@click="navigate"
											>
												<span class="font-medium">{{ node.name }}</span>
												<span v-if="node.condition" class="text-muted-foreground text-xs">{{ node.condition }}</span>
												<span class="text-muted-foreground ml-auto tabular-nums">{{ node.state }}</span>
												<ArrowRight class="text-state-complete size-4 shrink-0" aria-hidden="true" />
											</ZhPanel>
										</RouterLink>
									</ZhTooltip>
									<ZhPanel v-else as="p" class="text-muted-foreground flex items-center gap-3 text-sm">
										<span class="font-medium">{{ node.name }}</span>
										<span v-if="node.condition" class="text-xs">{{ node.condition }}</span>
										<span class="ml-auto">{{ node.state }}</span>
									</ZhPanel>
								</li>
							</ul>
						</ZhPanel>
					</Transition>
				</div>
				<!-- Top right: the model the run used, then what it has cost. -->
				<div class="ml-auto flex shrink-0 items-start gap-6 text-right">
					<div v-if="detail.run.model" data-testid="run-model">
						<ZhSystemLabel tone="system">Model</ZhSystemLabel>
						<p class="text-foreground text-sm font-medium">{{ detail.run.model }}</p>
						<p
							v-if="detail.modelStandsInFor"
							class="text-muted-foreground text-xs"
							data-testid="run-model-stand-in"
						>
							In place of {{ detail.modelStandsInFor }}, which this connection does not offer
						</p>
					</div>
					<ZhTooltip v-if="cost">
						<div data-testid="run-cost">
							<ZhSystemLabel tone="system">Cost</ZhSystemLabel>
							<p class="text-foreground text-2xl font-semibold tabular-nums">{{ cost.amount }}</p>
						</div>
						<template v-if="cost.lines.length > 0" #content>
							<p v-for="line in cost.lines" :key="line">{{ line }}</p>
						</template>
					</ZhTooltip>
				</div>
			</div>
			<div v-if="detail.execution" class="space-y-2">
				<p class="text-sm">
					<span class="text-muted-foreground">Execution </span>
					<RouterLink
						class="font-medium underline"
						data-testid="run-execution"
						:to="`/executions/${detail.execution.id}`"
					>{{ detail.execution.name }}</RouterLink>
				</p>
				<ExecutionResources :resources="detail.execution.resources" />
			</div>
			<p v-if="plan" class="text-sm">
				<span class="text-muted-foreground">Plan </span>
				<RouterLink class="font-medium underline" data-testid="run-plan" :to="`/plans/${plan.id}`">
					{{ plan.summary || 'Open the plan' }}
				</RouterLink>
			</p>
			<p v-if="detail.task" class="text-sm">
				<span class="text-muted-foreground">Task </span>
				<!-- A run of a linked clone names the owner's version it ran, not the clone's own counter (#1260). -->
				<template v-if="detail.task.ownerVersion">
					<ZhTooltip
						:text="detail.task.current ? 'The clone is still on this version of its owner’s task' : 'The clone has moved on to a newer version of its owner’s task'"
					>
						<RouterLink
							class="font-medium underline"
							data-testid="run-owner-version"
							:class="detail.task.current ? '' : 'text-state-active'"
							:to="{ path: `/tasks/${detail.task.id}`, query: { version: detail.task.versionId } }"
						>{{ ownerVersionLabel(detail.task.ownerVersion.ownerName, detail.task.name, detail.task.ownerVersion.version) }}</RouterLink>
					</ZhTooltip>
					<span class="text-muted-foreground">{{ detail.task.current ? ' (current)' : ' (older version)' }}</span>
				</template>
				<template v-else>
					<RouterLink class="font-medium underline" :to="`/tasks/${detail.task.id}`">{{ detail.task.name }}</RouterLink>
					<span class="text-muted-foreground"> · </span>
					<ZhTooltip
						:text="detail.task.current ? 'The task still runs this version' : 'The task has moved on to a newer version'"
					>
						<RouterLink
							class="underline"
							:class="detail.task.current ? '' : 'text-state-active'"
							:to="{ path: `/tasks/${detail.task.id}`, query: { version: detail.task.versionId } }"
						>
							v{{ detail.task.version ?? '?' }}{{ detail.task.current ? ' (current)' : ' (older version)' }}
						</RouterLink>
					</ZhTooltip>
				</template>
				<span class="text-muted-foreground font-mono text-xs"> {{ detail.task.slug }}</span>
			</p>
			<p v-else-if="detail.task === null" class="text-muted-foreground text-sm">Task deleted</p>
			<!-- Who asked for the work (#1373), or that nobody did: a schedule, a webhook, a token, or an older run. -->
			<p class="text-sm" data-testid="run-requested-by">
				<span class="text-muted-foreground">Asked for by </span>
				<span v-if="detail.run.requestedBy" class="font-medium">{{ detail.run.requestedBy.name }}</span>
				<span v-else class="text-muted-foreground">nobody</span>
			</p>
			<div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
				<ZhPageTitle kicker="Run" data-testid="run-status-title" :data-status="shownStatus">{{ shownStatus }}</ZhPageTitle>
				<ZhTooltip v-if="timing?.live" :text="`Started ${timing.startedAt}`">
					<p class="text-muted-foreground text-sm tabular-nums">
						running for <span class="text-foreground font-medium">{{ timing.duration }}</span>
					</p>
				</ZhTooltip>
				<ZhTooltip v-else-if="scheduled" :text="scheduled.at">
					<p class="text-muted-foreground text-sm tabular-nums">
						starts in <span class="text-foreground font-medium">{{ scheduled.in }}</span>
					</p>
				</ZhTooltip>
				<p v-else-if="timing" class="text-muted-foreground text-sm tabular-nums">
					started {{ timing.startedAt }} · took <span class="text-foreground font-medium">{{ timing.duration }}</span>
				</p>
			</div>
			<p class="text-muted-foreground text-sm">{{ detail.run.id }}</p>
			<div class="flex gap-2">
				<Button v-if="cancellable.has(detail.run.status)" type="button" variant="outline" :disabled="busy" @click="cancel">
					Cancel
				</Button>
				<Button
					v-if="retryable.has(detail.run.status)"
					type="button"
					variant="outline"
					:disabled="busy"
					@click="retry"
				>
					Retry as new run
				</Button>
				<Button v-if="detail.run.logArtifactKey" type="button" variant="outline" @click="toggleLog">
					{{ logOpen ? 'Hide log' : 'Show log' }}
				</Button>
			</div>
			<RetryCorrectionForm
				v-if="retryOpen"
				v-model="retryForm"
				:inputs="detail.inputs"
				:errors="retryErrors"
				:busy="busy"
				@submit="submitRetry"
				@close="retryOpen = false"
			/>
			<ZhJson v-if="logOpen && log !== null" :value="log" label="Run log" />
			<RunSignInPanel
				v-if="signInGate"
				:key="signInGate.id"
				:run-id="detail.run.id"
				:gate="signInGate"
				@handed-back="refresh"
			/>
			<GateDecisionCard
				v-for="gate in unlinkedGates"
				:key="gate.id"
				:gate="gate"
				:busy="busy"
				:decided="decisions.decidedOf(gate.id)"
				:decided-label="decisions.decidedLabelOf(gate.id)"
				data-testid="run-unlinked-gate"
				@decide="decide"
			>
				<template #payload>
					<!--
						A gate asks a human a question, and formatGatePayload surfaces the body/text field as prose
						when there is one. ZhJson renders that as text and anything else as a tree, so the question
						stays readable either way.
					-->
					<ZhJson :value="formatGatePayload(gate.payload).preview" label="Gate payload" />
				</template>
			</GateDecisionCard>
			<!-- Decided with an edit: what the run drafted, and what the CEO actually approved (os#911). -->
			<ZhPanel
				v-for="gate in editedGates"
				:key="`edited-${gate.id}`"
				data-testid="run-gate-edit"
				class="space-y-2"
			>
				<p class="text-sm font-medium">{{ toolLabel(gate.toolName, uiMode) }} — approved with an edit</p>
				<GatePayloadEditor
					:payload="gate.payload as Record<string, unknown>"
					:editable-fields="gate.editableFields ?? []"
					:edited-payload="gate.editedPayload as Record<string, unknown>"
				/>
			</ZhPanel>
			<ZhRunTimeline :steps="steps" label="Run events" class="run-timeline text-sm">
				<template #body="{ step }">
					<ZhJson v-if="stepOf(step)?.payload != null" :value="stepOf(step)?.payload" label="Step payload" />
				</template>
				<template #decide="{ step }">
					<GateDecisionCard
						v-if="gateOf(step)"
						:gate="gateOf(step) as GateRow"
						:busy="busy"
						:decided="decisions.decidedOf((gateOf(step) as GateRow).id)"
						:decided-label="decisions.decidedLabelOf((gateOf(step) as GateRow).id)"
						@decide="decide"
					/>
				</template>
			</ZhRunTimeline>
			<p v-if="terminalReason && !timeline.length" data-testid="run-timeline-reason" class="text-sm font-medium">
				{{ terminalReason.text }}
			</p>
		</template>
	</section>
</template>
